import * as React from "react";
import { Box, Maximize2, MonitorPlay, Pencil, Workflow } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Pill } from "@/components/ui/pill";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import type { LineView } from "@/types/scene.types";
import { DisplayLinksButton } from "@/features/display-tokens/DisplayLinksButton";
import { MachineSidebar } from "@/features/machine-sidebar/MachineSidebar";

export interface SidebarStateValue {
  nodeId: string;
  machineId: string | null;
  canvasAdd: boolean;
}

export interface PendingAssignmentValue {
  nodeId: string;
  machineId: string;
  name: string;
}

export interface FlowSidebarBlockProps {
  lineId: string;
  sidebarState: SidebarStateValue | null;
  onClose: () => void;
  onCreated: (assignment: PendingAssignmentValue) => void;
}

export function FlowSidebarBlock({
  lineId,
  sidebarState,
  onClose,
  onCreated,
}: FlowSidebarBlockProps): React.ReactElement | null {
  if (!sidebarState) return null;
  return (
    <MachineSidebar
      machineId={sidebarState.machineId}
      lineId={lineId}
      mode="edit"
      context={sidebarState.canvasAdd ? "canvas-add" : "line-edit"}
      provisoryNodeId={sidebarState.nodeId}
      onClose={onClose}
      onCancel={onClose}
      onMachineChange={(event) => {
        if (event.type === "created" && event.name) {
          onCreated({
            nodeId: sidebarState.nodeId,
            machineId: event.machineId,
            name: event.name,
          });
        } else if (event.type === "removed") {
          onClose();
        }
      }}
    />
  );
}

export interface FlowBandHeaderProps {
  machineCount: number;
  editMode: boolean;
  isGestor: boolean;
  onFullscreen: () => void;
  onStartEdit: () => void;
  // Spec 078 — alternância 2D/3D (FR-018). `sceneAvailable=false` ⇒ sem WebGL: o toggle some.
  view: LineView;
  onViewChange: (next: LineView) => void;
  sceneAvailable: boolean;
  // Spec 078 US5 — rota ambiente (todos) e links de exibição (GESTOR).
  lineId: string;
  lineName: string;
}

export function FlowBandHeader({
  machineCount,
  editMode,
  isGestor,
  onFullscreen,
  onStartEdit,
  view,
  onViewChange,
  sceneAvailable,
  lineId,
  lineName,
}: FlowBandHeaderProps): React.ReactElement {
  return (
    <header className="mb-3.5 flex flex-wrap items-center justify-between gap-4">
      <div className="flex items-center gap-3">
        <h2
          id="flow-section-card-h2"
          className="text-[19px] font-semibold tracking-[-0.015em] text-text-primary"
        >
          Fluxo de produção
        </h2>
        <Pill tone="neutral">
          {machineCount} {machineCount === 1 ? "máquina" : "máquinas"}
        </Pill>
      </div>
      <div className="flex flex-wrap items-center gap-4">
        <div className="flex flex-wrap items-center gap-3.5 text-[11.5px] text-text-secondary">
          <span className="inline-flex items-center gap-1.5">
            <span className="h-1.5 w-1.5 rounded-full bg-rd-success-500" />
            produção
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span className="h-1.5 w-1.5 rounded-full bg-rd-warning-500" />
            atenção
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span className="h-1.5 w-1.5 rounded-full bg-rd-danger-500" />
            sem comunicação
          </span>
        </div>
        {!editMode && sceneAvailable ? (
          <Tabs value={view} onValueChange={(next) => onViewChange(next as LineView)}>
            <TabsList variant="pill" aria-label="Visão do fluxo">
              <TabsTrigger value="2d">
                <Workflow className="mr-1 h-3.5 w-3.5" aria-hidden />
                2D
              </TabsTrigger>
              <TabsTrigger value="3d">
                <Box className="mr-1 h-3.5 w-3.5" aria-hidden />
                3D
              </TabsTrigger>
            </TabsList>
          </Tabs>
        ) : null}
        {!editMode && view === "3d" ? (
          <div className="flex gap-2">
            <Button asChild size="sm" variant="outline">
              <Link
                to={`/lines/${lineId}/ambient`}
                target="_blank"
                rel="noopener"
                aria-label="Modo TV"
              >
                <MonitorPlay className="h-4 w-4" aria-hidden />
                Modo TV
              </Link>
            </Button>
            {isGestor ? <DisplayLinksButton lineId={lineId} lineName={lineName} /> : null}
          </div>
        ) : null}
        {!editMode && view === "2d" ? (
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={onFullscreen} aria-label="Tela cheia">
              <Maximize2 className="h-4 w-4" />
              Tela cheia
            </Button>
            {isGestor ? (
              <Button size="sm" variant="outline" onClick={onStartEdit} aria-label="Editar fluxo">
                <Pencil className="h-4 w-4" />
                Editar fluxo
              </Button>
            ) : null}
          </div>
        ) : null}
      </div>
    </header>
  );
}
